import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { APP_PRIMARY, NEVADA_GRAY } from 'colours';
import { Grid, Drawer, Divider } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import useUsersApi from 'on_web/hooks/useUsersApi';
import auth0Client from 'on_web/Auth0';
import {
  ROUTE_ASSESSMENT_HOME,
  ROUTE_ASSESSMENT_HEALTH_RISKS_GROUPS,
  ROUTE_ASSESSMENT_DNA_TRAIT_GROUP,
  ROUTE_ASSESSMENT_RECOMMENDATIONS,
  ROUTE_ASSESSMENT_LIKELIHOOD,
  ROUTE_ASSESSMENT_HEALTH_RISKS,
  ROUTE_ASSESSMENT_DNA_RISKS,
  ROUTE_ASSESSMENT_TERMS_OF_SERVICE,
  ROUTE_ASSESSMENT_RESEARCH,
  ROUTE_ASSESSMENT_SHARE_APP,
  ROUTE_HEALTH_PROFILE,
  ROUTE_ASSESSMENT_FAQ,
} from 'routes';

import {
  MenuDnaIcon,
  MenuRecommendationsIcon,
  MenuSeverityIcon,
  MenuLikelihoodIcon,
  MenuSummaryIcon,
  MenuResearchIcon,
  MenuShareIcon,
  MenuSupportIcon,
  LogoutIcon,
  SupportIcon,
  HealthProfileIcon,
} from 'on_web/assets/iconsList';

import { lifenomeLogoHeader } from 'on_web/assets/imageList';

import {
  getHealthRisks,
  getComorbidityNameByCode,
  getDnaRisks,
  getDnaRiskNameByCode,
} from 'on_web/helpers/risksHelper';

import useCheckScreenSize from 'on_web/hooks/useCheckScreenSize';

import CustomMenuItem from 'on_web/components/shared/Menu/CustomMenuItem/CustomMenuItem';
import { trackEvent } from 'on_web/helpers/userTrackingEventsHelper';

function DesktopMenu(props) {
  const [activeIndex, setActiveIndex] = useState(
    props.id !== undefined ? props.id : null
  );
  const [subMenu, setSubMenu] = useState(null);
  const [activeSubMenuCode, setActiveSubMenuCode] = useState(
    props.submenuId !== undefined ? props.submenuId : null
  );
  const history = useHistory();
  const { t } = useTranslation();
  const [isDesktop] = useCheckScreenSize();

  const [{ logout }] = useUsersApi();

  useEffect(() => {
    if (props.id === 1) {
      setSubMenu(getHealthRisks(NEVADA_GRAY, '34px'));
    } else if (props.id === 2) {
      if (props.hasDnaProcessed) {
        setSubMenu(getDnaRisks(NEVADA_GRAY, '34px'));
      }
    } else {
      setSubMenu(null);
    }
  }, [logout]);

  useEffect(() => {
    if (props.id === 2) {
      if (props.hasDnaProcessed) {
        setSubMenu(
          getDnaRisks(NEVADA_GRAY, '34px').filter(r =>
            props.hasFullDna
              ? r.alwaysVisible != null
              : r.alwaysVisible === true
          )
        );
      }
    }
  }, [props.hasDnaProcessed]);

  const drawerWidth = 300;
  const useStyles = makeStyles(theme => ({
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
      zIndex: 9999,
    },
    drawerPaper: {
      width: drawerWidth,
    },
    logoIcon: {
      alignItems: 'flex-start',
      paddingLeft: 30,
      paddingBottom: 5,
    },
    menuItem: {
      height: '50px',
    },
    menuItemsText: {
      fontFamily: 'Museo-500',
      fontSize: '14px',
      lineHeight: '17px',
    },
    menuItemsIcons: {
      color: '#33b0ce',
      fontSize: '28px',
    },
  }));
  const classes = useStyles();

  const menuOptions = {
    assessmentOptions: [
      {
        id: 0,
        icon: (
          <MenuSummaryIcon
            color={activeIndex === 0 ? 'white' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.mainItems.home'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click HOME');
          setSubMenu(null);
          setActiveIndex(0);
          history.push({
            pathname: ROUTE_ASSESSMENT_HOME,
            state: props.stateToTransferOnNavigation,
          });
        },
      },
      {
        id: 1,
        icon: (
          <MenuSeverityIcon
            color={activeIndex === 1 ? 'white' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.mainItems.healthBasedRisks'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click HEALTH-BASED RISKS');
          setSubMenu(getHealthRisks(NEVADA_GRAY, '34px'));
          setActiveIndex(activeIndex === 1 ? null : 1);
          history.push({
            pathname: ROUTE_ASSESSMENT_HEALTH_RISKS,
            state: props.stateToTransferOnNavigation,
          });
        },
      },
      {
        id: 2,
        icon: (
          <MenuDnaIcon
            color={activeIndex === 2 ? 'white' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.mainItems.dnaBasedRisks'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click DNA-BASE RISKS');
          setSubMenu(getDnaRisks(NEVADA_GRAY, '34px'));
          setActiveIndex(activeIndex === 2 ? null : 2);
          history.push({
            pathname: ROUTE_ASSESSMENT_DNA_RISKS,
            state: props.stateToTransferOnNavigation,
          });
        },
      },
      {
        id: 3,
        icon: (
          <MenuLikelihoodIcon
            color={activeIndex === 3 ? 'white' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.mainItems.likelihoodHistory'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click LIKELIHOOD');
          setSubMenu(null);
          setActiveIndex(3);
          history.push({
            pathname: ROUTE_ASSESSMENT_LIKELIHOOD,
            state: props.stateToTransferOnNavigation,
          });
        },
      },
      {
        id: 4,
        icon: (
          <MenuRecommendationsIcon
            color={activeIndex === 4 ? 'white' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.mainItems.recommendations'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click RECOMMENDATIONS');
          setSubMenu(null);
          setActiveIndex(4);
          history.push({
            pathname: ROUTE_ASSESSMENT_RECOMMENDATIONS,
            state: props.stateToTransferOnNavigation,
          });
        },
      },
    ],
    profileOptions: [
      {
        id: 5,
        icon: (
          <HealthProfileIcon
            fill={activeIndex === 5 ? 'white' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.mainItems.profile'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click PROFILE');
          setSubMenu(null);
          setActiveIndex(5);
          history.push({
            pathname: ROUTE_HEALTH_PROFILE,
            state: props.stateToTransferOnNavigation,
          });
        },
      },
      /*{
        id: 6,
        icon: (
          <SettingsIcon
            fill={activeIndex === 5 ? 'white' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.mainItems.settings'),
        onClick: () => {trackEvent(
            'Reporting',
            'Menu click SETTINGS'
          );
          setSubMenu(null);
          setActiveIndex(activeIndex === 5 ? null : 5);
        },
      },*/
      {
        id: 7,
        icon: (
          <MenuResearchIcon
            color={activeIndex === 7 ? '#ffffff' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.research'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click RESEARCH');
          setSubMenu(null);
          setActiveIndex(7);
          history.push({
            pathname: ROUTE_ASSESSMENT_RESEARCH,
          });
        },
      },
      {
        id: 8,
        icon: (
          <MenuShareIcon
            color={activeIndex === 8 ? '#ffffff' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.shareApp'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click SHARE');
          setSubMenu(null);
          setActiveIndex(8);
          history.push({
            pathname: ROUTE_ASSESSMENT_SHARE_APP,
          });
        },
      },
      {
        id: 9,
        icon: (
          <MenuSupportIcon
            color={activeIndex === 9 ? '#ffffff' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.support'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click SUPPORT');
          setSubMenu(null);
          setActiveIndex(9);
          /*window.open(
            'mailto:covidsupport@lifenome.com?subject=Fix%20my%20issue!',
            '_blank'
          );*/
          history.push({
            pathname: ROUTE_ASSESSMENT_FAQ,
          });
        },
      },
      {
        id: 10,
        icon: (
          <LogoutIcon
            fill={activeIndex === 10 ? 'white' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.logout'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click LOGOUT');
          setSubMenu(null);
          setActiveIndex(10);
          handleLogout();
        },
      },
      /*{
        id: 8,
        icon: (
          <AppsSelectIcon
            fill={activeIndex === 7 ? 'white' : NEVADA_GRAY}
            width="34px"
            height="34px"
          />
        ),
        text: t('common.menu.mainItems.apps'),
        onClick: () => {
          trackEvent(
            'Reporting',
            'Menu click APPS'
          );
          setSubMenu(null);
          setActiveIndex(activeIndex === 7 ? null : 7);
        },
      },*/
    ],
    extraOptions: [
      {
        id: 11,
        text: t('common.menu.mainItems.about'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click ABOUT LIFENOME');
          setActiveIndex(11);
          window.open('https://www.lifenome.com/about/#vision', '_blank');
        },
      },
      {
        id: 12,
        text: t('common.menu.mainItems.termsOfService'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click TERMS OF SERVICE');
          setActiveIndex(12);
          history.push({
            pathname: ROUTE_ASSESSMENT_TERMS_OF_SERVICE,
          });
        },
      },
      {
        id: 13,
        text: t('common.menu.mainItems.legalPolicy'),
        onClick: () => {
          trackEvent('Reporting', 'Menu click PRIVACY POLICY');
          setActiveIndex(13);
          window.open('https://www.lifenome.com/privacy-policy/', '_blank');
        },
      },
    ],
  };

  const getSubMenuName = code => {
    if (activeIndex === 1) {
      return getComorbidityNameByCode(
        code,
        props.stateToTransferOnNavigation &&
          props.stateToTransferOnNavigation.healthBasedRisks
          ? props.stateToTransferOnNavigation.healthBasedRisks
          : null
      );
    } else if (activeIndex === 2) {
      return getDnaRiskNameByCode(code);
    } else {
      return '';
    }
  };

  const getPushData = code => {
    if (activeIndex === 1) {
      return {
        pathname: ROUTE_ASSESSMENT_HEALTH_RISKS_GROUPS,
        state: {
          group: code,
          response: props.stateToTransferOnNavigation,
        },
      };
    } else if (activeIndex === 2) {
      return {
        pathname: ROUTE_ASSESSMENT_DNA_TRAIT_GROUP,
        state: {
          group: code,
          hasFullDna: props.hasFullDna ? props.hasFullDna : null,
        },
      };
    } else {
      return {};
    }
  };

  const handleLogout = () => {
    logout();
    auth0Client.signOut();
  };

  return (
    <>
      {isDesktop ? (
        <Drawer
          className={classes.drawer}
          variant="persistent"
          anchor="left"
          open={true}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <Grid container className={classes.logoIcon}>
            <img
              src={lifenomeLogoHeader}
              width="200px"
              alt="Lifenome logo"
            ></img>
          </Grid>
          <Grid container style={{ height: `100%` }}>
            <Grid
              container
              direction="column"
              justify="space-between"
              style={{ height: 'auto' }}
            >
              <Grid item style={{ marginBottom: 60 }}>
                {menuOptions.assessmentOptions
                  /*.filter(op =>
                    process.env.REACT_APP_HIDE_DNA_SECTION !== 'true'
                      ? op.id >= 0
                      : op.id !== 2
                  )*/
                  .map((option, ix) => {
                    return (
                      <Grid item kex={ix}>
                        <CustomMenuItem
                          index={option.id}
                          backgroundColor={
                            activeIndex === option.id ? APP_PRIMARY : 'white'
                          }
                          icon={option.icon}
                          text={option.text}
                          textColor={
                            activeIndex === option.id ? 'white' : NEVADA_GRAY
                          }
                          upperCase
                          onClick={option.onClick}
                        />
                        <Grid container direction="column">
                          {ix === activeIndex &&
                            subMenu &&
                            subMenu.map(option => {
                              return (
                                <Grid item>
                                  <CustomMenuItem
                                    index={option.code}
                                    icon={option.icon}
                                    text={getSubMenuName(option.code)}
                                    textColor={
                                      activeIndex === option.id
                                        ? 'white'
                                        : NEVADA_GRAY
                                    }
                                    style={{
                                      paddingLeft: 20,
                                      borderBottom:
                                        activeSubMenuCode === option.code
                                          ? `3px solid ${APP_PRIMARY}`
                                          : 'white',
                                    }}
                                    onClick={() => {
                                      trackEvent(
                                        'Reporting',
                                        `Menu click ${option.code}`
                                      );
                                      setActiveSubMenuCode(option.code);
                                      history.push(getPushData(option.code));
                                    }}
                                  />
                                  <Divider />
                                </Grid>
                              );
                            })}
                        </Grid>
                        <Divider />
                      </Grid>
                    );
                  })}
              </Grid>
              <Grid item>
                <Divider />
                {menuOptions.profileOptions.map((option, ix) => {
                  return (
                    <div key={ix}>
                      <CustomMenuItem
                        index={option.id}
                        icon={option.icon}
                        backgroundColor={
                          activeIndex === option.id ? APP_PRIMARY : 'white'
                        }
                        text={option.text}
                        textColor={
                          activeIndex === option.id ? 'white' : NEVADA_GRAY
                        }
                        onClick={option.onClick}
                      />
                      <Divider />
                    </div>
                  );
                })}
                {menuOptions.extraOptions.map((option, ix) => {
                  return (
                    <div key={ix}>
                      <CustomMenuItem
                        index={option.id}
                        backgroundColor={
                          activeIndex === option.id ? APP_PRIMARY : 'white'
                        }
                        text={option.text}
                        textColor={
                          activeIndex === option.id ? 'white' : NEVADA_GRAY
                        }
                        onClick={option.onClick}
                      />
                    </div>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Drawer>
      ) : (
        <React.Fragment />
      )}
    </>
  );
}

export default DesktopMenu;
